import { ApiRequestError } from '@/common/apiRequestError';

export const NETWORK_ERROR_TYPE = 'urn:app:error:network';
const UNTITLED_ERROR_MESSAGE = 'An error occurred';

export class ApiError extends Error {
  readonly data: unknown;

  constructor(
    message: string,
    public readonly status: number,
    public readonly errorType: string,
    options?: { cause?: unknown; data?: unknown },
  ) {
    super(message, { cause: options?.cause });
    this.name = 'ApiError';
    this.data = options?.data;
  }
}

export class BadRequestError extends ApiError {
  constructor(message: string, errorType: string, data?: unknown) {
    super(message, 400, errorType, { data });
    this.name = 'BadRequestError';
  }
}

export class NotAuthenticatedError extends ApiError {
  constructor(message: string, errorType: string, data?: unknown) {
    super(message, 401, errorType, { data });
    this.name = 'NotAuthenticatedError';
  }
}

export class NotAuthorizedError extends ApiError {
  constructor(message: string, errorType: string, data?: unknown) {
    super(message, 403, errorType, { data });
    this.name = 'NotAuthorizedError';
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string, errorType: string, data?: unknown) {
    super(message, 404, errorType, { data });
    this.name = 'NotFoundError';
  }
}

export class ConflictError extends ApiError {
  constructor(message: string, errorType: string, data?: unknown) {
    super(message, 409, errorType, { data });
    this.name = 'ConflictError';
  }
}

export class ValidationError extends ApiError {
  constructor(message: string, errorType: string, data?: unknown) {
    super(message, 422, errorType, { data });
    this.name = 'ValidationError';
  }
}

export function isSdkError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

// Resource authorization sends `missing_permission:<name>[:job:<id>]` as the title, which is
// a code for logs, not a sentence for the recruiter reading the page.
function readableTitle(title: string | undefined): string | undefined {
  if (title?.startsWith('missing_permission:')) return "You don't have permission to do this.";
  return title;
}

// Transforms hey-api raw errors (RFC 7807 shape) into typed subclasses.
// body is the parsed response body; httpStatus is the authoritative HTTP status from Response.status.
export function rethrow(body: unknown, httpStatus: number): never {
  const raw = body && typeof body === 'object' ? (body as { type?: string; title?: string }) : {};
  const type = raw.type ?? 'urn:app:error:unknown';
  const title = readableTitle(raw.title) ?? UNTITLED_ERROR_MESSAGE;

  switch (httpStatus) {
    case 400:
      throw new BadRequestError(title, type, body);
    case 401:
      throw new NotAuthenticatedError(title, type, body);
    case 403:
      throw new NotAuthorizedError(title, type, body);
    case 404:
      throw new NotFoundError(title, type, body);
    case 409:
      throw new ConflictError(title, type, body);
    case 422:
      throw new ValidationError(title, type, body);
    default:
      throw new ApiError(title, httpStatus, type, { data: body });
  }
}

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError;
  }
}

export function extractErrorMessage(
  err: unknown,
  fallback = 'An error occurred. Please try again.',
): string {
  const e = err as
    | { data?: { detail?: unknown; message?: unknown }; message?: string }
    | null
    | undefined;

  // A FastAPI 422 sends `detail` as a list of rows, which is not text to render.
  const fromBody = [e?.data?.detail, e?.data?.message].find(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
  if (fromBody) return fromBody;

  // An ApiRequestError carries axios's wording ("Request failed with status code
  // 500") so error tracking gets a real message. That is not something to show a
  // candidate, so it takes the fallback instead.
  if (err instanceof ApiRequestError) return fallback;

  // Same rule for the generated client: with no title from the backend, the page's own words beat ours.
  if (err instanceof ApiError && (err.message === UNTITLED_ERROR_MESSAGE || err.errorType === NETWORK_ERROR_TYPE)) {
    return fallback;
  }

  return e?.message || fallback;
}
